"use client";
import { useEffect, useState } from "react";
import type { CatalogRow } from "../lib/catalog.js";
import { formatKopecks } from "../lib/money.js";
import { useGuestCart } from "../lib/use-guest-cart.js";
import { persistRefCode } from "../lib/ref-code.js";

// Витрина · экран 01 «Каталог» — визуальные ярлыки категорий (мокап docs/design/.../Витрина.dc.html).
// Фильтрация по категориям на этом маршруте НЕ подключена (нет category-данных, проводка вне scope),
// поэтому это статические, некликабельные ярлыки — витринный chrome, а не контролы.
const CATEGORY_LABELS = ["Помады", "Масла для бороды", "Гели для бритья", "Воски и глины", "Наборы"] as const;

export default function CatalogClient({ initial, refCode }: { initial: CatalogRow[]; refCode?: string }) {
  const [rows] = useState(initial);
  const { cart, add } = useGuestCart();
  const count = cart.items.reduce((s, i) => s + i.qty, 0);
  // атрибуция реферала: ?ref= на каталоге -> localStorage -> тело чекаута (см. lib/ref-code.ts)
  useEffect(() => { persistRefCode(refCode); }, [refCode]);

  return (
    <main>
      {/* Витрина-герой — тёмный «эспрессо» блок (экран 01) */}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          background: "var(--bar)",
          color: "var(--onbar)",
        }}
      >
        <div
          style={{
            padding: "clamp(32px, 5vw, 44px) clamp(20px, 4vw, 40px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div style={{ font: "600 11px var(--font-mono)", letterSpacing: ".2em", color: "var(--brassbr)" }}>
            БАРБЕР-АПТЕКА · С 1926
          </div>
          <h1
            className="display"
            style={{ font: "400 clamp(32px, 4.5vw, 46px)/1.05 var(--font-display)", margin: "14px 0 12px" }}
          >
            Помада, что держит весь день
          </h1>
          <p style={{ font: "400 14px/1.6 var(--font-body)", color: "var(--barmuted)", maxWidth: 400, margin: 0 }}>
            Пудовая фиксация, матовый блеск, аромат кедра и кожи. Собрано мастерами для мастеров.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
            <span style={{ font: "600 13px var(--font-body)", background: "var(--brass)", color: "var(--bar)", padding: "12px 22px", borderRadius: 5 }}>
              Смотреть помады
            </span>
            <span style={{ font: "600 13px var(--font-body)", border: "1.5px solid var(--barfield)", color: "var(--onbar)", padding: "11px 22px", borderRadius: 5 }}>
              Наборы
            </span>
          </div>
        </div>
        <div className="ph" style={{ minHeight: 260 }}>витрина · фото банки помады</div>
      </section>

      {/* Тулбар каталога — ярлыки категорий + ссылка на корзину со счётчиком */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "clamp(20px, 3vw, 26px) clamp(16px, 4vw, 40px) clamp(28px, 5vw, 40px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="pill cat-on">Все</span>
            {CATEGORY_LABELS.map((label) => (
              <span key={label} className="pill cat-off">{label}</span>
            ))}
          </div>
          <a
            href="/cart"
            data-testid="cart-link"
            aria-label={`Корзина, товаров: ${count}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              font: "600 13px var(--font-body)",
              color: "var(--ink)",
              textDecoration: "none",
              background: "var(--surface)",
              border: "1px solid var(--fieldbd)",
              borderRadius: 5,
              padding: "9px 14px",
              whiteSpace: "nowrap",
            }}
          >
            Корзина
            <span
              data-testid="cart-count"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 20,
                height: 20,
                padding: "0 6px",
                borderRadius: 999,
                background: "var(--primary)",
                color: "var(--onprimary)",
                font: "600 11px var(--font-mono)",
              }}
            >
              {count}
            </span>
          </a>
        </div>

        {/* Сетка товаров — 4 колонки на десктопе, схлопывается к 1 на мобиле */}
        {rows.length === 0 ? (
          <p className="muted" style={{ padding: "48px 0", textAlign: "center", font: "400 14px var(--font-body)" }}>
            Каталог пуст
          </p>
        ) : (
          <ul
            aria-label="Товары"
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {rows.map((p) => {
              const disabled = p.available <= 0 || p.displayPriceKopecks === null;
              return (
                <li key={p.id} data-testid={`product-${p.id}`} className="prod" style={{ display: "flex", flexDirection: "column" }}>
                  <div className="ph" style={{ aspectRatio: "1" }}>фото</div>
                  <div style={{ padding: 12, display: "flex", flexDirection: "column", flex: 1 }}>
                    {p.sku && (
                      <div
                        className="kacc"
                        style={{ fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 500, letterSpacing: ".08em", textTransform: "uppercase" }}
                      >
                        {p.sku}
                      </div>
                    )}
                    <h2 style={{ font: "600 14px var(--font-body)", margin: "5px 0 0" }}>{p.name}</h2>
                    <p
                      aria-label="Доступно"
                      className="muted"
                      style={{ font: "400 11px var(--font-body)", margin: "6px 0 0" }}
                    >
                      {p.available > 0 ? `В наличии: ${p.available}` : "Нет в наличии"}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto", paddingTop: 10 }}>
                      {p.displayPriceKopecks === null ? (
                        <span data-testid={`price-${p.id}`} className="muted" style={{ font: "400 12px var(--font-body)" }}>
                          Цена по запросу
                        </span>
                      ) : (
                        <span data-testid={`price-${p.id}`} className="price" style={{ fontSize: 15, fontWeight: 600 }}>
                          {formatKopecks(p.displayPriceKopecks)}
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`add-${p.id}`}
                        className="add"
                        disabled={disabled}
                        onClick={() => add(p.id)}
                        style={disabled ? { background: "var(--border)", color: "var(--faint)", cursor: "default" } : undefined}
                      >
                        В корзину
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
